import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { RequestStatus, REQUEST_STATUSES } from 'stores/common';
import type { RootState } from 'stores/store';
import { User } from 'stores/user/userTypes';
import dayjs from 'dayjs';
import { Patient } from 'stores/patients/patientsTypes';
import {
  getUserRates,
  getUsers,
  patchUser,
  patchUserNote,
} from './adminClient';

export interface AdminState {
  list?: User[];
  listRequestStatus?: RequestStatus;
  patchRequestStatus?: RequestStatus;
  patchNoteRequestStatus?: RequestStatus;
  rates: Record<string, Patient[]>;
  ratesRequestStatus: Record<string, RequestStatus>;
}

const initialState: AdminState = {
  rates: {},
  ratesRequestStatus: {},
};

export const getUsersAsync = createAsyncThunk('admin/list', () => getUsers());
export const patchUserAsync = createAsyncThunk('admin/patch', (user: User) => patchUser(user));
export const patchUserNoteAsync = createAsyncThunk('admin/patchNote', (
  { userWPId, note }: {userWPId: string, note: string},
) => patchUserNote(userWPId, note));

export const getUserRatesAsync = createAsyncThunk(
  'admin/userRates',
  ({ userWPId }: { userWPId: string }) => {
    const startDate = dayjs().startOf('day').format('YYYY-MM-DD');
    const endDate = dayjs().add(14, 'day').endOf('day').format('YYYY-MM-DD');

    return getUserRates(userWPId, new URLSearchParams({
      start_date: startDate,
      end_date: endDate,
    }));
  },
);

export const adminSlice = createSlice({
  name: 'admin',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getUsersAsync.pending, (state) => {
        state.listRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(getUsersAsync.fulfilled, (state, { payload }) => {
        state.listRequestStatus = REQUEST_STATUSES.SUCCESS;
        state.list = payload;
      })
      .addCase(getUsersAsync.rejected, (state) => {
        state.listRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(getUserRatesAsync.pending, (state, { meta }) => {
        state.ratesRequestStatus[meta.arg.userWPId] = REQUEST_STATUSES.PENDING;
      })
      .addCase(getUserRatesAsync.fulfilled, (state, { payload, meta }) => {
        state.ratesRequestStatus[meta.arg.userWPId] = REQUEST_STATUSES.SUCCESS;
        state.rates[meta.arg.userWPId] = payload;
      })
      .addCase(getUserRatesAsync.rejected, (state, { meta }) => {
        state.ratesRequestStatus[meta.arg.userWPId] = REQUEST_STATUSES.ERROR;
      })
      .addCase(patchUserAsync.pending, (state) => {
        state.patchRequestStatus = REQUEST_STATUSES.PENDING;
      })
      .addCase(patchUserAsync.fulfilled, (state) => {
        state.patchRequestStatus = REQUEST_STATUSES.SUCCESS;
      })
      .addCase(patchUserAsync.rejected, (state) => {
        state.patchRequestStatus = REQUEST_STATUSES.ERROR;
      })
      .addCase(patchUserNoteAsync.pending, (state, { meta }) => {
        state.patchNoteRequestStatus = REQUEST_STATUSES.PENDING;
        const index = state.list?.findIndex((user) => user.wp_id === meta.arg.userWPId);
        if (index && index > -1 && state.list) {
          state.list[index].note = meta.arg.note;
        }
      })
      .addCase(patchUserNoteAsync.fulfilled, (state) => {
        state.patchNoteRequestStatus = REQUEST_STATUSES.SUCCESS;
      })
      .addCase(patchUserNoteAsync.rejected, (state) => {
        state.patchNoteRequestStatus = REQUEST_STATUSES.ERROR;
      });
  },
});

export const getUserList = (state: RootState) => state.admin.list;
export const getUserListRequestStatus = (state: RootState) => state.admin.listRequestStatus;
export const getUserRatesList = (state: RootState) => state.admin.rates;
export const getUserRatesListRequestStatus = (state: RootState) => state.admin.ratesRequestStatus;

export default adminSlice.reducer;
